import { AdminCreateCouponRequest } from '@fsp/types';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { apiClient } from '../../../services/apiClient';

interface CreateCouponDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CURRENCIES = ['AUD', 'USD', 'EUR', 'GBP', 'NZD'];

const initialFormState = {
  name: '',
  discountType: 'percent' as 'percent' | 'amount',
  percentOff: '',
  amountOff: '',
  currency: 'AUD',
  duration: 'once' as 'once' | 'repeating' | 'forever',
  durationInMonths: '',
  maxRedemptions: '',
  redeemBy: '',
  promotionCode: '',
  promotionCodeMaxRedemptions: '',
};

export default function CreateCouponDialog({ open, onClose, onSuccess }: CreateCouponDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initialFormState);
      setError(null);
    }
  }, [open]);

  const updateForm = (updates: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  const isFormValid = () => {
    if (form.discountType === 'percent') {
      const val = Number(form.percentOff);
      if (!val || val < 1 || val > 100) return false;
    } else {
      const val = Number(form.amountOff);
      if (!val || val <= 0) return false;
      if (!form.currency) return false;
    }

    if (form.duration === 'repeating') {
      const val = Number(form.durationInMonths);
      if (!val || val < 1) return false;
    }

    if (!form.promotionCode.trim()) return false;

    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const body: AdminCreateCouponRequest = {
        discountType: form.discountType,
        duration: form.duration,
        ...(form.name.trim() && { name: form.name.trim() }),
        ...(form.discountType === 'percent' && { percentOff: Number(form.percentOff) }),
        ...(form.discountType === 'amount' && {
          amountOff: Number(form.amountOff),
          currency: form.currency.toLowerCase(),
        }),
        ...(form.duration === 'repeating' && { durationInMonths: Number(form.durationInMonths) }),
        ...(form.maxRedemptions && { maxRedemptions: Number(form.maxRedemptions) }),
        ...(form.redeemBy && { redeemBy: form.redeemBy }),
        promotionCode: {
          code: form.promotionCode.trim(),
          ...(form.promotionCodeMaxRedemptions && {
            maxRedemptions: Number(form.promotionCodeMaxRedemptions),
          }),
        },
      };

      const response = await apiClient.post('/admin/coupons', body);

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to create coupon');
      }

      enqueueSnackbar('Coupon created successfully', { variant: 'success' });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Coupon</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => updateForm({ name: e.target.value })}
            placeholder="e.g. Summer Sale 20%"
            helperText="Optional display name for the coupon"
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Discount Type</InputLabel>
            <Select
              value={form.discountType}
              label="Discount Type"
              onChange={(e) =>
                updateForm({ discountType: e.target.value as 'percent' | 'amount' })
              }
            >
              <MenuItem value="percent">Percentage</MenuItem>
              <MenuItem value="amount">Fixed Amount</MenuItem>
            </Select>
          </FormControl>

          {form.discountType === 'percent' ? (
            <TextField
              label="Percentage Off"
              type="number"
              value={form.percentOff}
              onChange={(e) => updateForm({ percentOff: e.target.value })}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                inputProps: { min: 1, max: 100 },
              }}
              required
              fullWidth
            />
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Amount Off"
                type="number"
                value={form.amountOff}
                onChange={(e) => updateForm({ amountOff: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0.01, step: 0.01 },
                }}
                required
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={form.currency}
                  label="Currency"
                  onChange={(e) => updateForm({ currency: e.target.value })}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <FormControl fullWidth>
            <InputLabel>Duration</InputLabel>
            <Select
              value={form.duration}
              label="Duration"
              onChange={(e) =>
                updateForm({ duration: e.target.value as 'once' | 'repeating' | 'forever' })
              }
            >
              <MenuItem value="once">Once</MenuItem>
              <MenuItem value="repeating">Repeating</MenuItem>
              <MenuItem value="forever">Forever</MenuItem>
            </Select>
          </FormControl>

          {form.duration === 'repeating' && (
            <TextField
              label="Duration in Months"
              type="number"
              value={form.durationInMonths}
              onChange={(e) => updateForm({ durationInMonths: e.target.value })}
              InputProps={{ inputProps: { min: 1 } }}
              required
              fullWidth
            />
          )}

          <TextField
            label="Max Redemptions"
            type="number"
            value={form.maxRedemptions}
            onChange={(e) => updateForm({ maxRedemptions: e.target.value })}
            helperText="Leave empty for unlimited"
            InputProps={{ inputProps: { min: 1 } }}
            fullWidth
          />

          <TextField
            label="Redeem By"
            type="date"
            value={form.redeemBy}
            onChange={(e) => updateForm({ redeemBy: e.target.value })}
            helperText="Leave empty for no expiry"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <Divider />

          <TextField
            label="Promotion Code"
            value={form.promotionCode}
            onChange={(e) => updateForm({ promotionCode: e.target.value.toUpperCase() })}
            placeholder="e.g. SUMMER20"
            helperText="The code customers will enter at checkout"
            required
            fullWidth
          />

          <TextField
            label="Promo Code Max Redemptions"
            type="number"
            value={form.promotionCodeMaxRedemptions}
            onChange={(e) => updateForm({ promotionCodeMaxRedemptions: e.target.value })}
            helperText="Leave empty for unlimited"
            InputProps={{ inputProps: { min: 1 } }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !isFormValid()}
        >
          {submitting ? 'Creating...' : 'Create Coupon'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
